import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  label: string;
  value: string;
}

interface ComboboxProps {
  /** Displayed text (the label of the current value). */
  value: string;
  /** Called with the picked option, or { label: text, value: text } for free text. */
  onChange: (option: ComboboxOption) => void;
  options: ComboboxOption[];
  placeholder?: string;
  loading?: boolean;
  /** Message when nothing matches the typed text. */
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

const MAX_VISIBLE = 50;
const PANEL_MAX_HEIGHT = 240; // max-h-60

/**
 * Text input with a suggestion dropdown. Typing filters the options; picking
 * one commits its real value, while arbitrary text is kept as-is (label=value).
 *
 * The dropdown renders in a body portal with fixed positioning, so it is never
 * clipped by overflow containers; it flips above the input near the viewport
 * bottom. (Rule of thumb for this codebase: overlays — dropdowns, tooltips,
 * popovers — always portal to body, never render inside overflow containers.)
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  loading = false,
  emptyText = 'No matches — press Enter to use the typed value',
  disabled = false,
  className,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    // Show the full list until the user actually types (opening with a
    // selected value must not collapse the list to just that value)
    const q = value.trim().toLowerCase();
    if (!hasTyped || !q) return options.slice(0, MAX_VISIBLE);
    // Coerce defensively — Cluvio options can contain non-string values
    return options
      .filter((o) => {
        const label = String(o.label ?? '');
        const val = String(o.value ?? '');
        return label.toLowerCase().includes(q) || val.toLowerCase().includes(q);
      })
      .slice(0, MAX_VISIBLE);
  }, [options, value, hasTyped]);

  useEffect(() => setHighlight(0), [value]);

  const openList = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (rect) {
      const up = rect.bottom + PANEL_MAX_HEIGHT + 8 > window.innerHeight && rect.top > PANEL_MAX_HEIGHT + 8;
      setPos({ left: rect.left, width: rect.width, top: up ? rect.top : rect.bottom, up });
    }
    setHasTyped(false);
    setOpen(true);
  };

  const close = () => setOpen(false);

  // Outside click (trigger + panel are exempt), Escape, page scroll, resize
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onScroll = (e: Event) => {
      // The panel's own internal scrolling must not close it
      if (panelRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const pick = (option: ComboboxOption) => {
    onChange(option);
    setHasTyped(false);
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          onChange({ label: e.target.value, value: e.target.value });
          setHasTyped(true);
          if (!open) openList();
        }}
        onFocus={openList}
        // Focus persists after a mouse pick (preventDefault on the option),
        // so clicking must also reopen — onFocus won't fire twice.
        onClick={() => {
          if (!open) openList();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) openList();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && filtered[highlight]) {
              e.preventDefault();
              pick(filtered[highlight]);
            } else {
              setOpen(false);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-background px-3 pr-8 text-sm shadow-card transition-colors',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-ring',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
      />
      <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
      </div>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            style={
              pos.up
                ? { left: pos.left, width: pos.width, bottom: window.innerHeight - pos.top + 4 }
                : { left: pos.left, width: pos.width, top: pos.top + 4 }
            }
            className="fixed z-[100] max-h-60 overflow-auto rounded-md border bg-popover p-1 shadow-pop animate-scale-in"
          >
            {loading && options.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">Loading options…</div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {options.length === 0
                  ? 'No predefined options for this filter — enter a value manually'
                  : emptyText}
              </div>
            ) : (
              filtered.map((o, i) => (
                <button
                  key={`${o.value}-${i}`}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(o);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
                    i === highlight ? 'bg-accent text-accent-foreground' : 'text-foreground'
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {o.label !== o.value && (
                    <span className="ml-2 shrink-0 text-[10px] text-muted-foreground truncate">{o.value}</span>
                  )}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
